import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, formatErr } from '@/lib/api';
import { PageShell } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, Phone, FileText, Save, User, Briefcase, DollarSign, CalendarClock } from 'lucide-react';
import CallLogDialog from '@/components/CallLogDialog';

const STATUS = ['sourced', 'lineup', 'interview', 'selected', 'offered', 'joined', 'rejected'];
const STATUS_LABEL = {
  sourced: 'New',
  lineup: 'In Progress',
  interview: 'Interview Scheduled',
  selected: 'Selected',
  offered: 'Offered',
  joined: 'Joined',
  rejected: 'Rejected',
};
const STATUS_COLOR = {
  sourced: 'bg-zinc-500/15 text-zinc-600 border-zinc-500/30',
  lineup: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  interview: 'bg-purple-500/15 text-purple-600 border-purple-500/30',
  selected: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  offered: 'bg-primary/15 text-primary border-primary/30',
  joined: 'bg-emerald-600/20 text-emerald-700 border-emerald-600/40',
  rejected: 'bg-red-500/15 text-red-600 border-red-500/30',
};
const OUTCOME_LABEL = { connected: 'Connected', not_answered: 'Not Answered', callback: 'Callback', not_interested: 'Not Interested' };
const OUTCOME_COLOR = {
  connected: 'text-emerald-600 border-emerald-500/40 bg-emerald-500/10',
  not_answered: 'text-amber-600 border-amber-500/40 bg-amber-500/10',
  callback: 'text-blue-600 border-blue-500/40 bg-blue-500/10',
  not_interested: 'text-red-600 border-red-500/40 bg-red-500/10',
};

function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(v) { return v ? new Date(v).toISOString() : ''; }
function fmt(iso) { if (!iso) return '—'; try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } }
function secToMMSS(s) { s = Math.max(0, Math.floor(s || 0)); const m = Math.floor(s / 60); return `${m}m ${s % 60}s`; }

export default function LineupProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cand, setCand] = useState(null);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [callOpen, setCallOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: c }, { data: L }] = await Promise.all([
        api.get(`/candidates/id/${id}`),
        api.get('/call-logs', { params: { candidate_type: 'candidate', candidate_id: id } }),
      ]);
      setCand(c);
      setForm(c);
      setLogs(L);
      // Log profile open for admin activity tracking (silent).
      api.post('/activity', { action: 'opened_profile', target_type: 'candidate', target_id: c.id, target_name: c.name }).catch(() => {});
    } catch (e) { toast.error(formatErr(e)); nav('/lineups'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/candidates/${id}`, form);
      toast.success('Saved'); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const downloadCv = async () => {
    if (!cand?.resume_path) return;
    try {
      const res = await api.get(`/files/${cand.resume_path}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = cand.resume_filename || 'resume';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!cand) return <PageShell><div className="text-muted-foreground">Loading…</div></PageShell>;

  const totalCalls = logs.length;
  const connected = logs.filter(l => l.outcome === 'connected').length;
  const totalSec = logs.reduce((s, l) => s + (l.duration_seconds || 0), 0);

  return (
    <PageShell>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Button variant="ghost" onClick={() => nav('/lineups')} data-testid="profile-back" className="h-10"><ArrowLeft className="w-4 h-4 mr-1" />All lineups</Button>
        <Badge variant="outline" className={STATUS_COLOR[cand.status]}>{STATUS_LABEL[cand.status] || cand.status}</Badge>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Owner: {cand.owner_name}</div>
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="font-display text-3xl sm:text-4xl font-black tracking-tight">{cand.name}</div>
          <div className="text-sm text-muted-foreground mt-1">{cand.designation || '—'}{cand.current_company ? ` · ${cand.current_company}` : ''}</div>
          <div className="flex gap-3 mt-2 text-sm">
            {cand.phone && <a href={`tel:${cand.phone}`} className="text-primary hover:underline font-mono">{cand.phone}</a>}
            {cand.email && <a href={`mailto:${cand.email}`} className="hover:underline">{cand.email}</a>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {cand.resume_path && <Button variant="outline" onClick={downloadCv} data-testid="profile-cv" className="h-11"><FileText className="w-4 h-4 mr-2" />Download CV</Button>}
          <Button onClick={() => setCallOpen(true)} data-testid="profile-call" className="h-11 bg-emerald-500 hover:bg-emerald-600 text-white"><Phone className="w-4 h-4 mr-2" />Call &amp; log</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="tactical-card p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Calls</div><div className="stat-num text-primary">{totalCalls}</div></div>
        <div className="tactical-card p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Connected</div><div className="stat-num">{connected}</div></div>
        <div className="tactical-card p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Talk time</div><div className="stat-num">{secToMMSS(totalSec)}</div></div>
        <div className="tactical-card p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Follow-up</div><div className="text-sm font-medium mt-1">{cand.next_follow_up ? fmt(cand.next_follow_up) : '—'}</div></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Personal */}
        <div className="tactical-card p-5">
          <div className="flex items-center gap-2 mb-3"><User className="w-4 h-4 text-primary" /><div className="font-display text-lg font-black tracking-tight">Personal</div></div>
          <div className="space-y-3">
            <div><Label className="text-[10px] uppercase tracking-widest">Name</Label><Input value={form.name || ''} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Phone</Label><Input value={form.phone || ''} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Alternate phone</Label><Input data-testid="profile-alternate-phone" value={form.alternate_phone || ''} onChange={(e) => setForm(f => ({ ...f, alternate_phone: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Email</Label><Input value={form.email || ''} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Location</Label><Input value={form.location || ''} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Full address</Label><Input data-testid="profile-full-address" value={form.full_address || ''} onChange={(e) => setForm(f => ({ ...f, full_address: e.target.value }))} className="h-11" /></div>
          </div>
        </div>

        {/* Employment */}
        <div className="tactical-card p-5">
          <div className="flex items-center gap-2 mb-3"><Briefcase className="w-4 h-4 text-primary" /><div className="font-display text-lg font-black tracking-tight">Employment</div></div>
          <div className="space-y-3">
            <div><Label className="text-[10px] uppercase tracking-widest">Current role</Label><Input value={form.designation || ''} onChange={(e) => setForm(f => ({ ...f, designation: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Current company</Label><Input value={form.current_company || ''} onChange={(e) => setForm(f => ({ ...f, current_company: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Experience</Label><Input value={form.experience || ''} onChange={(e) => setForm(f => ({ ...f, experience: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Relevant experience</Label><Input data-testid="profile-relevant-experience" value={form.relevant_experience || ''} onChange={(e) => setForm(f => ({ ...f, relevant_experience: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Notice period</Label><Input value={form.notice_period || ''} onChange={(e) => setForm(f => ({ ...f, notice_period: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Employment type</Label><Input data-testid="profile-employment-type" value={form.employment_type || ''} onChange={(e) => setForm(f => ({ ...f, employment_type: e.target.value }))} className="h-11" /></div>
          </div>
        </div>

        {/* Money + Pipeline */}
        <div className="tactical-card p-5">
          <div className="flex items-center gap-2 mb-3"><DollarSign className="w-4 h-4 text-primary" /><div className="font-display text-lg font-black tracking-tight">Compensation &amp; Pipeline</div></div>
          <div className="space-y-3">
            <div><Label className="text-[10px] uppercase tracking-widest">CTC</Label><Input value={form.ctc || ''} onChange={(e) => setForm(f => ({ ...f, ctc: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Expected CTC</Label><Input value={form.ectc || ''} onChange={(e) => setForm(f => ({ ...f, ectc: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Status</Label>
              <Select value={form.status || 'sourced'} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger className="h-11" data-testid="profile-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s] || s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase tracking-widest">Client</Label><Input value={form.client_name || ''} onChange={(e) => setForm(f => ({ ...f, client_name: e.target.value }))} className="h-11" /></div>
            <div><Label className="text-[10px] uppercase tracking-widest">Preferred location</Label><Input data-testid="profile-preferred-location" value={form.preferred_location || ''} onChange={(e) => setForm(f => ({ ...f, preferred_location: e.target.value }))} className="h-11" /></div>
          </div>
        </div>
      </div>

      <div className="tactical-card p-5 mb-6" data-testid="profile-cv-details">
        <div className="font-display text-lg font-black tracking-tight mb-3">Parsed CV details</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Professional summary</Label>
            <Textarea data-testid="profile-professional-summary" rows={3} value={form.professional_summary || ''} onChange={(e) => setForm(f => ({ ...f, professional_summary: e.target.value }))} />
          </div>
          <div><Label className="text-[10px] uppercase tracking-widest">LinkedIn</Label><Input data-testid="profile-linkedin" value={form.linkedin_url || ''} onChange={(e) => setForm(f => ({ ...f, linkedin_url: e.target.value }))} className="h-11" /></div>
          <div><Label className="text-[10px] uppercase tracking-widest">Portfolio</Label><Input data-testid="profile-portfolio" value={form.portfolio_url || ''} onChange={(e) => setForm(f => ({ ...f, portfolio_url: e.target.value }))} className="h-11" /></div>
          <div><Label className="text-[10px] uppercase tracking-widest">GitHub</Label><Input data-testid="profile-github" value={form.github_url || ''} onChange={(e) => setForm(f => ({ ...f, github_url: e.target.value }))} className="h-11" /></div>
          <div><Label className="text-[10px] uppercase tracking-widest">Languages</Label><Input data-testid="profile-languages" value={form.languages || ''} onChange={(e) => setForm(f => ({ ...f, languages: e.target.value }))} className="h-11" /></div>
          <div className="md:col-span-2"><Label className="text-[10px] uppercase tracking-widest">Education</Label><Textarea data-testid="profile-education" rows={2} value={form.education || ''} onChange={(e) => setForm(f => ({ ...f, education: e.target.value }))} /></div>
          <div className="md:col-span-2"><Label className="text-[10px] uppercase tracking-widest">Certifications</Label><Textarea data-testid="profile-certifications" rows={2} value={form.certifications || ''} onChange={(e) => setForm(f => ({ ...f, certifications: e.target.value }))} /></div>
          <div className="md:col-span-2"><Label className="text-[10px] uppercase tracking-widest">Projects</Label><Textarea data-testid="profile-projects" rows={2} value={form.projects || ''} onChange={(e) => setForm(f => ({ ...f, projects: e.target.value }))} /></div>
          <div className="md:col-span-2"><Label className="text-[10px] uppercase tracking-widest">Achievements</Label><Textarea data-testid="profile-achievements" rows={2} value={form.achievements || ''} onChange={(e) => setForm(f => ({ ...f, achievements: e.target.value }))} /></div>
        </div>

        <div className="mt-5 border-t border-border pt-4" data-testid="profile-work-history">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
            Work history
          </div>
          {Array.isArray(form.work_history) && form.work_history.length > 0 ? (
            <div className="space-y-3">
              {form.work_history.map((job, index) => (
                <div
                  key={`${job.company}-${job.role}-${index}`}
                  className="border border-border p-3 rounded-md"
                >
                  <div
                    className="font-medium"
                    data-testid={`profile-work-history-role-${index}`}
                  >
                    {job.role || 'Role not stated'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {job.company || 'Company not stated'} · {job.location || 'Location not stated'}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {job.duration || `${job.start_date || '—'} to ${job.end_date || 'Present'}`}
                  </div>
                  {job.summary && <div className="mt-2 text-sm">{job.summary}</div>}
                  {Array.isArray(job.highlights) && job.highlights.length > 0 && (
                    <ul className="mt-2 list-disc pl-5 text-sm space-y-1">
                      {job.highlights.map((highlight, highlightIndex) => (
                        <li key={`${index}-${highlightIndex}`}>{highlight}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div
              className="text-sm text-muted-foreground"
              data-testid="profile-work-history-empty"
            >
              No structured work history was found in this CV. Add it manually if needed.
            </div>
          )}
        </div>
      </div>

      {/* Follow-up + notes */}
      <div className="tactical-card p-5 mb-6">
        <div className="flex items-center gap-2 mb-3"><CalendarClock className="w-4 h-4 text-primary" /><div className="font-display text-lg font-black tracking-tight">Follow-up &amp; Notes</div></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label className="text-[10px] uppercase tracking-widest">Lineup / added at</Label><Input type="datetime-local" value={isoToLocalInput(form.lineup_datetime)} onChange={(e) => setForm(f => ({ ...f, lineup_datetime: localInputToIso(e.target.value) }))} className="h-11" /></div>
          <div><Label className="text-[10px] uppercase tracking-widest">Next follow-up</Label><Input type="datetime-local" data-testid="profile-followup" value={isoToLocalInput(form.next_follow_up)} onChange={(e) => setForm(f => ({ ...f, next_follow_up: localInputToIso(e.target.value) }))} className="h-11" /></div>
          <div><Label className="text-[10px] uppercase tracking-widest">Source portal</Label><Input value={form.source_portal || ''} onChange={(e) => setForm(f => ({ ...f, source_portal: e.target.value }))} className="h-11" /></div>
          <div className="md:col-span-3"><Label className="text-[10px] uppercase tracking-widest">Skills</Label><Textarea rows={2} value={form.skills || ''} onChange={(e) => setForm(f => ({ ...f, skills: e.target.value }))} /></div>
          <div className="md:col-span-3"><Label className="text-[10px] uppercase tracking-widest">Notes</Label><Textarea rows={3} value={form.notes || ''} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="profile-save" className="mt-4 h-11 bg-primary hover:bg-primary/90 text-white"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save changes'}</Button>
      </div>

      {/* Call history */}
      <div className="tactical-card p-5">
        <div className="flex items-center gap-2 mb-3"><Phone className="w-4 h-4 text-primary" /><div className="font-display text-lg font-black tracking-tight">Call history · {logs.length} entries</div></div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3" data-testid="profile-calls-mobile">
          {logs.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No calls yet. Tap "Call & log" above to record the first one.</div>}
          {logs.map(l => (
            <div key={l.id} className="border border-border/70 rounded-md p-3 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><div className="text-sm font-medium truncate">{l.recruiter_name}</div><div className="text-[11px] text-muted-foreground">{fmt(l.started_at)} → {fmt(l.ended_at)}</div></div>
                <Badge variant="outline" className={OUTCOME_COLOR[l.outcome]}>{OUTCOME_LABEL[l.outcome]}</Badge>
              </div>
              <div className="text-[11px] flex items-center justify-between text-muted-foreground"><span>Duration</span><span className="font-mono font-black text-foreground">{secToMMSS(l.duration_seconds)}</span></div>
              {l.notes && <div className="text-xs pt-1 border-t border-border/40 mt-1">{l.notes}</div>}
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recruiter</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Ended</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="profile-calls">
              {logs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No calls yet.</TableCell></TableRow>}
              {logs.map(l => (
                <TableRow key={l.id}>
                  <TableCell className="text-sm">{l.recruiter_name}</TableCell>
                  <TableCell className="text-xs">{fmt(l.started_at)}</TableCell>
                  <TableCell className="text-xs">{fmt(l.ended_at)}</TableCell>
                  <TableCell className="font-mono">{secToMMSS(l.duration_seconds)}</TableCell>
                  <TableCell><Badge variant="outline" className={OUTCOME_COLOR[l.outcome]}>{OUTCOME_LABEL[l.outcome]}</Badge></TableCell>
                  <TableCell className="text-xs max-w-[320px] truncate" title={l.notes}>{l.notes || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <CallLogDialog
        open={callOpen}
        onOpenChange={setCallOpen}
        candidate={{ id: cand.id, name: cand.name, phone: cand.phone }}
        candidateType="candidate"
        defaultDialer
        onSaved={() => { setCallOpen(false); load(); }}
      />
    </PageShell>
  );
}
